import { Tag } from "@fluentui/react-components";
import { useCategories } from "hooks/useCategories";
import { ICategory } from "models/ICategory";
import { ITag } from "models/ITag";
import { IUser } from "models/IUser";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { renderTemplateIcon } from "utils/templateIcons";
import { useSharedTagStyles } from "theme/sharedStyles";
import { ViewEntityDialog } from "components/viewDialog/ViewEntityDialog";
import {
  IViewEntityField,
  IViewEntityTab,
} from "components/viewDialog/ViewEntityDialog.types";
import { useEditTemplateDialogStyles } from "./EditTemplateDialog.styles";
import { IViewTemplateDialogProps } from "./ViewTemplateDialog.types";

export const ViewTemplateDialog = ({
  template,
  isOpen,
  onClose,
}: IViewTemplateDialogProps): JSX.Element => {
  const styles = useEditTemplateDialogStyles();
  const tagStyles = useSharedTagStyles();

  const users: IUser[] = useSelector(
    (state: RootState) => state.usersSlice.value,
  );

  const { data: categories } = useCategories();

  const allTags =
    categories?.flatMap((category: ICategory) =>
      category.tags.map((tag: ITag) => ({
        id: tag.id,
        name: tag.name,
      })),
    ) || [];

  const resolveUserName = (userId: string): string => {
    const user = users.find((u: IUser) => u.id === userId);
    return user?.displayName || user?.mail || userId;
  };

  const resolveTagName = (tagId: string): string => {
    const tag = allTags.find(
      (t: { id: string; name: string }) => t.id === tagId,
    );
    return tag?.name || tagId;
  };

  const detailsFields: IViewEntityField[] = [
    {
      label: "Icon",
      value: renderTemplateIcon(template.icon),
    },
    {
      label: "Title",
      value: template.name,
    },
    {
      label: "Description",
      value: template.description || "-",
    },
    {
      label: "Prompt",
      value: template.prefilledPrompt || "-",
    },
  ];

  const configurationFields: IViewEntityField[] = [
    {
      label: "Recommended LLM",
      value: template.recommendedLLM,
    },
  ];

  if (template.suggestedKnowledgeComponents?.length > 0) {
    configurationFields.push({
      label: "Knowledge Components",
      value: (
        <div className={styles.knowledgeComponentsGrid}>
          {template.suggestedKnowledgeComponents.map((kcId) => (
            <Tag key={kcId} className={tagStyles.tag} appearance="filled">
              {resolveTagName(kcId)}
            </Tag>
          ))}
        </div>
      ),
    });
  } else {
    configurationFields.push({
      label: "Knowledge Components",
      value: "-",
    });
  }

  configurationFields.push({
    label: "Status",
    value: `${template.isActive ? "Active" : "Inactive"}${
      template.showOnChatScreen ? " | Shown on Chat Screen" : " | Gallery only"
    }`,
  });

  configurationFields.push({
    label: "Access Control",
    value:
      template.accessControl === "full"
        ? "Full - All users"
        : "Limited - Specific users",
  });

  if (
    template.accessControl === "limited" &&
    template.allowedUsers?.length > 0
  ) {
    configurationFields.push({
      label: "Allowed Users",
      value: (
        <div className={styles.knowledgeComponentsGrid}>
          {template.allowedUsers.map((userId) => (
            <Tag key={userId} className={tagStyles.tag} appearance="outline">
              {resolveUserName(userId)}
            </Tag>
          ))}
        </div>
      ),
    });
  }

  const tabs: IViewEntityTab[] = [
    {
      key: "details",
      label: "Details",
      fields: detailsFields,
    },
    {
      key: "configuration",
      label: "Configuration",
      fields: configurationFields,
    },
  ];

  return (
    <ViewEntityDialog
      isOpen={isOpen}
      onDismiss={onClose}
      title={`${template.name}`}
      tabs={tabs}
    />
  );
};
